import type { FaqEntriesByLocale } from './faq';

export type { FaqEntry } from './faq';

/**
 * ### SAFETY_FAQ_BY_LOCALE
 *
 * Listen AI Safety FAQ copy, rendered on the solution page and emitted as
 * FAQPage structured data from the same strings so the two can never drift.
 * Editing an entry changes both surfaces at once.
 *
 * Entries are ordered the way a public-sector buyer qualifies the solution:
 * whether it can go where cameras cannot, what it hears, what it does not
 * do with speech, how false alerts are kept down, how alerts arrive, how it
 * is procured, and finally how well it performs.
 *
 * Two rules hold throughout. The only performance figure is the
 * TTA-certified F1-score of 98% or more, the one Safety figure cleared for
 * publication, and it is never merged with an Industrial figure. Nothing
 * here promises a capability the shipping product does not have: the sound
 * list names only detected classes (fourteen in Korean, twelve in English,
 * which leaves out the Korean rescue phrases), alert delivery is the monitoring pop-up
 * alone, and procurement is described as the open route through KONEPS
 * rather than the pilot program, which a given institution can join only
 * once.
 *
 * @see {@link buildSafetyStructuredData} Wraps these entries as schema.org Question nodes
 */
export const SAFETY_FAQ_BY_LOCALE: FaqEntriesByLocale = {
  ko: [
    {
      question:
        '화장실이나 탈의실처럼 CCTV를 둘 수 없는 곳에도 설치할 수 있나요?',
      answer:
        '가능합니다. Listen AI Safety는 영상이 아니라 소리로 위험 상황을 감지하므로 화장실, 탈의실, 사우나처럼 카메라를 설치할 수 없는 공간에 적합합니다. 정부청사관리본부 체육관과 내장산국립공원 공중화장실 등 실제 현장에서도 이런 공간을 중심으로 운영하고 있습니다.',
    },
    {
      question: 'Listen AI Safety는 어떤 소리를 감지하나요?',
      answer:
        'Listen AI Safety는 “살려주세요”, “도와주세요” 같은 구조 요청 음성과 비명, 고성, 울음·흐느낌, 헐떡거림, 유리 깨짐, 물리적 충격, 사이렌, 차 경적 등 14종의 소리를 감지합니다. 현장별로 감지할 소리와 임계값을 마이크 단위로 설정할 수 있습니다.',
    },
    {
      question: '대화 내용을 녹음하거나 분석하나요?',
      answer:
        'Listen AI Safety의 목적은 위험 상황을 나타내는 소리의 종류를 감지하는 것입니다. 소리는 현장의 AI SOUND BOX 안에서 분석되고 분석이 끝난 데이터는 바로 파기되며, 고객 시스템에는 감지된 소리 종류만 전달되고 소리 원본은 전송되지 않습니다. 마이크 연결과 이벤트 전달은 물리적으로 분리된 네트워크 인터페이스를 사용하며, 통신비밀보호법 제14조 관련 법률 검토를 마쳤습니다.',
    },
    {
      question: '잘못된 알림(오탐)은 어떻게 줄이나요?',
      answer:
        '복합 이벤트 기능으로 줄입니다. 현장에 맞는 조건을 설정해 비명, 구조 요청, 충격음 같은 여러 신호가 정해진 시간 안에 함께 감지될 때 하나의 위험 상황으로 판단하므로, 단발성 소리로 생기는 불필요한 알림이 줄어듭니다. 마이크별 임계값 조정과 관리자가 직접 들어보고 판단하는 기능도 함께 제공합니다.',
    },
    {
      question: '알림은 어떤 방식으로 받을 수 있나요?',
      answer:
        '모니터링 시스템의 실시간 팝업으로 알림을 받습니다. Open API와 ONVIF·MQTT 프로토콜을 지원해 기존 VMS나 관제 시스템, 비상벨과 연결할 수 있고, 하드웨어에 직접 넣는 Embedded SDK는 별도 협의로 제공합니다.',
    },
    {
      question: '공공기관은 어떤 경로로 도입할 수 있나요?',
      answer:
        'Listen AI Safety는 조달청 혁신제품으로 지정되어 있어, 공공기관은 나라장터를 통해 구매할 수 있습니다. 제품의 상세 정보와 구매 조건은 조달청 혁신장터에서 확인할 수 있습니다.',
    },
    {
      question: '감지 정확도는 어느 정도인가요?',
      answer:
        'Listen AI Safety의 위험 소리 감지 성능은 TTA(한국정보통신기술협회) 시험 인증에서 F1-score 98% 이상으로 확인되었습니다. 현장의 소음 환경에 따라 결과가 달라질 수 있으므로 도입 전에 현장 조건에 맞춘 설정과 검증을 함께 진행합니다.',
    },
  ],
  en: [
    {
      question:
        'Can it be installed where CCTV is not allowed, such as restrooms and changing rooms?',
      answer:
        'Yes. Listen AI Safety recognises danger by sound rather than video, which makes it suited to restrooms, changing rooms, saunas, and other spaces where cameras cannot be installed. Live deployments such as the Government Buildings Management Office gymnasium and the public restrooms of Naejangsan National Park centre on exactly these spaces.',
    },
    {
      question: 'Which sounds does Listen AI Safety detect?',
      answer:
        'Listen AI Safety detects 12 sounds, among them screams, aggressive yelling, crying and sobbing, heavy breathing, breaking glass, physical impacts, sirens, and car horns. Which sounds are detected, and at what threshold, is set per microphone for each site.',
    },
    {
      question: 'Does it record or analyse conversations?',
      answer:
        'The purpose of Listen AI Safety is to detect the kinds of sounds that indicate danger. Sound is analysed inside the on-site AI SOUND BOX and discarded as soon as analysis is complete; only the detected sound type reaches the customer system, never the recording itself. Microphone input and event delivery run over physically separate network interfaces, and a legal review against Article 14 of the Korean Protection of Communications Secrets Act has been completed.',
    },
    {
      question: 'How does it keep false alerts down?',
      answer:
        'Through composite events. Conditions set for each site combine several detections, such as a scream, aggressive yelling, and an impact within a set number of seconds, into one danger situation, so one-off sounds no longer raise unnecessary alerts. Per-microphone thresholds and the ability for operators to listen and judge for themselves are provided as well.',
    },
    {
      question: 'How are alerts delivered?',
      answer:
        'Alerts appear as real-time pop-ups in the monitoring system. The Open API and the ONVIF and MQTT protocols connect Listen AI Safety to existing VMS and control-room systems and to emergency call buttons, and an embedded SDK for hardware integration is available by arrangement.',
    },
    {
      question: 'How can a public institution procure it?',
      answer:
        'Listen AI Safety is a designated Innovative Product of the Korean Public Procurement Service, so public institutions can purchase it through the KONEPS national procurement system. Full product details and purchasing terms are available on the Public Procurement Service innovation marketplace.',
    },
    {
      question: 'How accurate is the detection?',
      answer:
        'The danger-sound detection performance of Listen AI Safety was verified at an F1-score of 98% or higher in TTA (Telecommunications Technology Association) test certification. Results vary with the noise conditions of each site, so configuration and validation for the site are carried out before rollout.',
    },
  ],
};
